import { useAnimate } from "framer-motion";
import React, { useEffect, useRef } from "react";

const MouseImageTrail = ({
  children,
  // List of image sources
  images,
  // Will render a new image every X pixels between mouse moves
  renderImageBuffer,
  // images will be rotated at a random number between zero and rotationRange,
  // alternating between a positive and negative rotation
  rotationRange,
}) => {
  const [scope, animate] = useAnimate();

  const lastRenderPosition = useRef({ x: 0, y: 0 });
  const imageRenderCount = useRef(0);
  // slot index -> the animations running for the picture currently in that slot
  const slotAnimations = useRef({});

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const handleMouseMove = (e) => {
    const { clientX, clientY } = e;

    const distance = calculateDistance(
      clientX,
      clientY,
      lastRenderPosition.current.x,
      lastRenderPosition.current.y
    );

    if (distance >= renderImageBuffer && !reducedMotion) {
      lastRenderPosition.current.x = clientX;
      lastRenderPosition.current.y = clientY;

      renderNextImage();
    }
  };

  const calculateDistance = (x1, y1, x2, y2) => {
    const deltaX = x2 - x1;
    const deltaY = y2 - y1;

    // Using the Pythagorean theorem to calculate the distance
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    return distance;
  };

  // listen on the window so the trail works over the whole screen, not just the container
  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  });

  const renderNextImage = () => {
    const imageIndex = imageRenderCount.current % images.length;
    const selector = `[data-mouse-move-index="${imageIndex}"]`;

    const el = document.querySelector(selector);

    // Max count reached: this slot still shows an older picture. Fade that one out where it is,
    // like the timed despawn, instead of letting it vanish when the slot is reused.
    const shown = parseFloat(getComputedStyle(el).opacity);
    if (shown > 0.02) {
      const ghost = el.cloneNode(true);
      ghost.removeAttribute("data-mouse-move-index");
      el.parentNode.appendChild(ghost);
      animate(ghost, { opacity: [shown, 0] }, { ease: "linear", duration: 0.5 }).then(() => ghost.remove());
    }
    // cancel what the old picture still had scheduled (its timed fade would hit the new picture)
    (slotAnimations.current[imageIndex] || []).forEach((controls) => controls.stop());

    el.style.top = `${lastRenderPosition.current.y}px`;
    el.style.left = `${lastRenderPosition.current.x}px`;
    el.style.zIndex = imageRenderCount.current.toString();

    const rotation = Math.random() * rotationRange;

    const appear = animate(
      selector,
      {
        opacity: [0, 1],
        transform: [
          `translate(-50%, -25%) scale(0.5) ${
            imageIndex % 2
              ? `rotate(${rotation}deg)`
              : `rotate(-${rotation}deg)`
          }`,
          `translate(-50%, -50%) scale(1) ${
            imageIndex % 2
              ? `rotate(-${rotation}deg)`
              : `rotate(${rotation}deg)`
          }`,
        ],
      },
      { type: "spring", damping: 15, stiffness: 200 }
    );

    const despawn = animate(
      selector,
      {
        opacity: [1, 0],
      },
      { ease: "linear", duration: 0.5, delay: 8 }
    );
    slotAnimations.current[imageIndex] = [appear, despawn];

    if (imageRenderCount.current < 1000) imageRenderCount.current++;
    else imageRenderCount.current = 0;
    
  };

  return (
    <div ref={scope} className="relative z-0">
      {children}

      {images.map((img, index) => (
        <img
          className="pointer-events-none fixed left-0 top-0 h-48 w-auto rounded-xl border-8 border-emerald-900 bg-emerald-900 object-cover opacity-0 z-0"
          src={img}
          alt=""
          aria-hidden="true"
          key={index}
          data-mouse-move-index={index}
        />
      ))}
    </div>
  );
};

export default MouseImageTrail